import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, Empty, Btn } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { timeAgo } from "@/lib/ui";
import { MonitorSmartphone, Search, RefreshCw, Play, Trash2, ScrollText, X, Sparkles, ShieldCheck, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { onLive } from "@/lib/live";
import { PatchManager } from "@/components/PatchManager";

const FIELD_OPTS = [["hostname", "Hostname"], ["os", "OS"], ["customer_name", "Customer"], ["local_ip", "Local IP"],
  ["logged_in_user", "User"], ["device_type", "Device Type"], ["status", "Status"], ["agent_version", "Agent Version"],
  ["serial_number", "Serial"], ["os_edition", "OS Edition"], ["site_name", "Site"]];

function deviceCategory(r) {
  if (r.device_type === "server") return "Server";
  const os = (r.os || "").toLowerCase();
  if (os.includes("mac") || os.includes("os x")) return "Mac";
  if (os.includes("windows")) return "PC";
  if (["linux", "ubuntu", "debian", "cent", "fedora", "red hat"].some((k) => os.includes(k))) return "Linux";
  return "Other";
}
function advFieldVal(r, row) {
  const key = row.field === "__custom" ? row.customKey : row.field;
  if (!key) return "";
  const v = r.custom_fields && r.custom_fields[key] != null ? r.custom_fields[key] : r[key];
  return Array.isArray(v) ? v.join(" ") : v == null ? "" : String(v);
}
function advMatch(r, row) {
  const v = advFieldVal(r, row).toLowerCase();
  const t = (row.value || "").toLowerCase();
  if (row.op === "is_empty") return v === "";
  if (row.op === "not_empty") return v !== "";
  if (row.op === "equals") return v === t;
  if (row.op === "not_equals") return v !== t;
  return v.includes(t);
}

export default function Devices() {  const nav = useNavigate();
  const { can } = useAuth();
  const [sp] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState(sp.get("status") || "");
  const [sel, setSel] = useState(new Set());
  const [patchDev, setPatchDev] = useState(null);
  const [scripts, setScripts] = useState([]);
  const [runOpen, setRunOpen] = useState(false);
  const [runScript, setRunScript] = useState("");
  const [aiOpen, setAiOpen] = useState(false);
  const [aiQ, setAiQ] = useState("");
  const [aiRes, setAiRes] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [typeF, setTypeF] = useState("");
  const [availF, setAvailF] = useState("");
  const [advOpen, setAdvOpen] = useState(false);
  const [advMode, setAdvMode] = useState("AND");
  const [advRows, setAdvRows] = useState([{ field: "hostname", op: "contains", value: "", customKey: "" }]);
  const setRow = (i, patch) => setAdvRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setAdvRows((rs) => [...rs, { field: "hostname", op: "contains", value: "", customKey: "" }]);
  const removeRow = (i) => setAdvRows((rs) => (rs.length === 1 ? rs : rs.filter((_, idx) => idx !== i)));
  const clearAdv = () => setAdvRows([{ field: "hostname", op: "contains", value: "", customKey: "" }]);

  const askAi = async () => {
    if (!aiQ.trim()) return;
    setAiLoading(true); setAiRes(null);
    try { const r = await api.post("/copilot/fleet-search", { query: aiQ }); setAiRes(r.data); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setAiLoading(false); }
  };

  const load = () => {
    const params = {};
    if (statusF) params.status = statusF;
    if (sp.get("site_id")) params.site_id = sp.get("site_id");
    if (sp.get("customer_id")) params.customer_id = sp.get("customer_id");
    api.get("/devices", { params }).then((r) => setRows(r.data)).catch(() => setRows([]));
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); const off = onLive((e) => { if (e.type === "device" || e.type === "job") load(); }); return () => { clearInterval(t); off(); }; }, [statusF, sp]);
  useEffect(() => { api.get("/scripts").then((r) => setScripts(r.data)).catch(() => {}); }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    let out = rows;
    if (q) {
      const s = q.toLowerCase();
      out = out.filter((r) => [r.hostname, r.customer_name, r.local_ip, r.os, r.logged_in_user, (r.tags || []).join(" ")]
        .some((v) => (v || "").toLowerCase().includes(s)));
    }
    if (typeF) out = out.filter((r) => deviceCategory(r) === typeF);
    if (availF) out = out.filter((r) => (availF === "online" ? r.status === "ONLINE" : r.status !== "ONLINE"));
    const active = advRows.filter((rw) => ((rw.field === "__custom" ? rw.customKey : rw.field) &&
      (rw.value || rw.op === "is_empty" || rw.op === "not_empty")));
    if (active.length) out = out.filter((r) => (advMode === "AND" ? active.every((rw) => advMatch(r, rw)) : active.some((rw) => advMatch(r, rw))));
    return out;
  }, [rows, q, typeF, availF, advRows, advMode]);

  const toggle = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSel(sel.size === filtered.length ? new Set() : new Set(filtered.map((r) => r.id)));

  const runOnSelected = async () => {
    try {
      const res = await api.post("/jobs/run-script", { script_id: runScript, device_ids: [...sel], confirm: true });
      toast.success(`Job dispatched to ${res.data.target_count} device(s)`);
      setRunOpen(false); setSel(new Set());
    } catch (e) { toast.error(apiErr(e)); }
  };
  const rebootSelected = async () => {
    if (!window.confirm(`Restart ${sel.size} device(s)? Users may have unsaved work.`)) return;
    for (const id of sel) { try { await api.post(`/devices/${id}/power`, { action: "restart" }); } catch (e) { console.error(`Restart failed for device ${id}:`, e); } }
    toast.success(`Restart queued for ${sel.size} device(s)`); setSel(new Set()); load();
  };

  if (!rows) return <div className="p-6"><Spinner /></div>;

  return (
    <div className="p-6 fade-up pb-24">
      <PageHeader title="Devices" subtitle={`${filtered.length} of ${rows.length} endpoints`} testid="devices-page"
        actions={<>
          {can("copilot:use") && <Btn variant="ghost" data-testid="ai-search-btn" onClick={() => setAiOpen(true)}><Sparkles size={14} /> Ask AI</Btn>}
          <Btn variant="ghost" onClick={load}><RefreshCw size={14} /> Refresh</Btn>
        </>} />

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex items-center gap-2 bg-slate-900 border border-slate-800 rounded-md px-3 py-1.5 flex-1 max-w-md">
          <Search size={14} className="text-slate-500" />
          <input data-testid="devices-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter hostname, IP, user, tag…"
            className="bg-transparent text-sm text-slate-100 outline-none flex-1 placeholder:text-slate-600" />
        </div>
        <select data-testid="devices-type-filter" value={typeF} onChange={(e) => setTypeF(e.target.value)}
          className="bg-slate-900 border border-slate-800 rounded-md px-3 py-1.5 text-sm text-slate-200 outline-none">
          <option value="">All types</option>
          {["PC", "Mac", "Server", "Linux", "Other"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select data-testid="devices-availability-filter" value={availF} onChange={(e) => setAvailF(e.target.value)}
          className="bg-slate-900 border border-slate-800 rounded-md px-3 py-1.5 text-sm text-slate-200 outline-none">
          <option value="">Any availability</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
        </select>
        <select data-testid="devices-status-filter" value={statusF} onChange={(e) => setStatusF(e.target.value)}
          className="bg-slate-900 border border-slate-800 rounded-md px-3 py-1.5 text-sm text-slate-200 outline-none">
          <option value="">All statuses</option>
          {["ONLINE", "OFFLINE", "DEGRADED", "MAINTENANCE", "UNKNOWN"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <Btn variant="ghost" data-testid="devices-advanced-toggle" onClick={() => setAdvOpen((o) => !o)}><SlidersHorizontal size={14} /> Advanced</Btn>
      </div>

      {advOpen && (
        <div className="card-surface rounded-lg p-3 mb-3" data-testid="devices-advanced-panel">
          <div className="flex items-center gap-2 mb-2 text-[12px] text-slate-400">
            Match
            <select data-testid="adv-mode" value={advMode} onChange={(e) => setAdvMode(e.target.value)} className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-[12px] text-slate-200 outline-none">
              <option value="AND">ALL (AND)</option><option value="OR">ANY (OR)</option>
            </select>
            of the following conditions:
          </div>
          {advRows.map((row, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2 mb-2" data-testid="adv-row">
              <select data-testid="adv-field" value={row.field} onChange={(e) => setRow(i, { field: e.target.value })} className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-[13px] text-slate-200 outline-none">
                {FIELD_OPTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                <option value="__custom">Custom field…</option>
              </select>
              {row.field === "__custom" && <input data-testid="adv-custom-key" value={row.customKey} onChange={(e) => setRow(i, { customKey: e.target.value })} placeholder="field key" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-[13px] text-slate-200 outline-none w-32 font-mono" />}
              <select data-testid="adv-op" value={row.op} onChange={(e) => setRow(i, { op: e.target.value })} className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-[13px] text-slate-200 outline-none">
                <option value="contains">contains</option><option value="equals">equals</option>
                <option value="not_equals">does not equal</option><option value="is_empty">is empty</option><option value="not_empty">is not empty</option>
              </select>
              {!["is_empty", "not_empty"].includes(row.op) && <input data-testid="adv-value" value={row.value} onChange={(e) => setRow(i, { value: e.target.value })} placeholder="value" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-[13px] text-slate-200 outline-none flex-1 min-w-[120px]" />}
              <button data-testid="adv-remove" onClick={() => removeRow(i)} className="text-slate-500 hover:text-rose-400 p-1"><X size={14} /></button>
            </div>
          ))}
          <div className="flex items-center gap-3 mt-1">
            <button data-testid="adv-add-row" onClick={addRow} className="text-[12px] text-cyan-300 hover:text-cyan-200">+ Add condition</button>
            <button data-testid="adv-clear" onClick={clearAdv} className="text-[12px] text-slate-500 hover:text-slate-300">Clear</button>
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <Empty icon={MonitorSmartphone} title="No devices" hint="Generate an enrollment token from a customer, then run the agent (or simulator) to enroll a device." />
      ) : (<>
        <div className="hidden md:block card-surface rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="devices-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-900/80 border-b border-slate-800">
                  <th className="px-3 py-2"><input type="checkbox" checked={sel.size === filtered.length && filtered.length > 0} onChange={toggleAll} /></th>
                  <th className="px-3 py-2">Status</th><th className="px-3 py-2">Device</th><th className="px-3 py-2">Customer</th>
                  <th className="px-3 py-2">Type</th><th className="px-3 py-2">OS</th><th className="px-3 py-2">IP</th>
                  <th className="px-3 py-2">User</th><th className="px-3 py-2">Agent</th><th className="px-3 py-2">Alerts</th><th className="px-3 py-2">Last Seen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {filtered.map((r) => (
                  <tr key={r.id} data-testid="device-row" className="hover-row cursor-pointer text-[13px]" onClick={() => nav("/devices/" + r.id)}>
                    <td className="px-3 py-1.5" onClick={(e) => { e.stopPropagation(); toggle(r.id); }}>
                      <input type="checkbox" checked={sel.has(r.id)} onChange={() => {}} /></td>
                    <td className="px-3 py-1.5"><StatusBadge status={r.status} small /></td>
                    <td className="px-3 py-1.5 font-mono text-slate-100">{r.hostname}
                      {r.pending_reboot && <span className="ml-1.5 text-[9px] bg-amber-500/20 text-amber-300 rounded px-1">reboot</span>}
                      <button data-testid={`device-patches-${r.id}`} title="Patch management" onClick={(e) => { e.stopPropagation(); setPatchDev({ id: r.id, hostname: r.hostname }); }} className="ml-2 text-slate-500 hover:text-cyan-300 align-middle"><ShieldCheck size={13} /></button></td>
                    <td className="px-3 py-1.5 text-slate-400">{r.customer_name}</td>
                    <td className="px-3 py-1.5 text-slate-400 capitalize">{r.device_type}</td>
                    <td className="px-3 py-1.5 text-slate-400">{r.os}</td>
                    <td className="px-3 py-1.5 font-mono text-slate-500">{r.local_ip || "—"}</td>
                    <td className="px-3 py-1.5 text-slate-400">{r.logged_in_user || "—"}</td>
                    <td className="px-3 py-1.5 font-mono text-slate-500">{r.agent_version}</td>
                    <td className="px-3 py-1.5">{r.open_alerts > 0 ? <span className="text-rose-400 font-mono">{r.open_alerts}</span> : <span className="text-slate-600">0</span>}</td>
                    <td className="px-3 py-1.5 text-slate-500">{timeAgo(r.last_seen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="md:hidden space-y-2" data-testid="devices-cards">
          {filtered.map((r) => (
            <div key={r.id} data-testid="device-card" onClick={() => nav("/devices/" + r.id)} className={"w-full text-left card-surface rounded-lg p-3 cursor-pointer " + (sel.has(r.id) ? "ring-1 ring-cyan-600" : "")}>
              <div className="flex items-center gap-2">
                {(can("scripts:execute") || can("power:restart")) && <input type="checkbox" data-testid={`device-card-select-${r.id}`} checked={sel.has(r.id)} onClick={(e) => e.stopPropagation()} onChange={() => toggle(r.id)} className="accent-cyan-500 shrink-0" />}
                <StatusBadge status={r.status} small /><span className="font-mono text-slate-100 text-[14px] flex-1 truncate">{r.hostname}</span>{r.open_alerts > 0 && <span className="text-rose-400 text-[12px] font-mono">{r.open_alerts} alert</span>}{r.pending_reboot && <span className="text-[9px] bg-amber-500/20 text-amber-300 rounded px-1">reboot</span>}</div>
              <div className="text-[12px] text-slate-400 mt-1 flex flex-wrap gap-x-3 gap-y-0.5"><span>{r.customer_name}</span><span>{r.os}</span><span className="font-mono">{r.local_ip || "—"}</span></div>
              <div className="text-[11px] text-slate-500 mt-1">{r.logged_in_user || "no user"} · seen {timeAgo(r.last_seen)}</div>
            </div>
          ))}
        </div>
      </>)}

      {patchDev && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-auto" onClick={() => setPatchDev(null)}>
          <div className="w-full max-w-6xl my-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end mb-2"><button data-testid="patch-modal-close" onClick={() => setPatchDev(null)} className="text-slate-300 hover:text-white bg-slate-800 rounded-full p-1.5"><X size={16} /></button></div>
            <PatchManager deviceId={patchDev.id} hostname={patchDev.hostname} canInstall={can("patch:install")} />
          </div>
        </div>
      )}

      {sel.size > 0 && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 flex flex-wrap justify-center items-center gap-3 card-surface rounded-full px-5 py-2.5 shadow-2xl border border-cyan-800/50 z-40 fade-up max-w-[92vw]" data-testid="bulk-action-bar">
          <span className="text-[13px] text-slate-300 font-medium">{sel.size} selected</span>
          {can("scripts:execute") && <Btn variant="ghost" onClick={() => setRunOpen(true)}><ScrollText size={14} /> Run Script</Btn>}
          {can("power:restart") && <Btn variant="ghost" onClick={rebootSelected}><RefreshCw size={14} /> Restart</Btn>}
          <button onClick={() => setSel(new Set())} className="text-slate-500 hover:text-slate-200"><X size={16} /></button>
        </div>
      )}

      {runOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setRunOpen(false)}>
          <div className="card-surface rounded-xl p-5 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading font-bold text-slate-100 mb-1">Run Script on {sel.size} device(s)</h2>
            <p className="text-[12px] text-amber-400 mb-3">This action will execute on {sel.size} endpoint(s).</p>
            <select data-testid="bulk-script-select" value={runScript} onChange={(e) => setRunScript(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 outline-none mb-4">
              <option value="">Select script…</option>
              {scripts.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.language} · {s.risk}</option>)}
            </select>
            <div className="flex justify-end gap-2"><Btn variant="ghost" onClick={() => setRunOpen(false)}>Cancel</Btn><Btn onClick={runOnSelected} disabled={!runScript}><Play size={14} /> Execute</Btn></div>
          </div>
        </div>
      )}
      {aiOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 pt-24" onClick={() => setAiOpen(false)}>
          <div className="card-surface rounded-xl p-5 w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()} data-testid="ai-search-modal">
            <div className="flex items-center gap-2 mb-3"><Sparkles size={16} className="text-cyan-400" /><h2 className="font-heading font-bold text-slate-100">Ask AI about your fleet</h2><button onClick={() => setAiOpen(false)} className="ml-auto text-slate-500 hover:text-slate-200"><X size={18} /></button></div>
            <div className="flex gap-2 mb-3">
              <input data-testid="ai-search-input" value={aiQ} onChange={(e) => setAiQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && askAi()} autoFocus
                placeholder="e.g. which servers are offline or low on disk?" className="flex-1 bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 outline-none" />
              <Btn data-testid="ai-search-run" onClick={askAi} disabled={aiLoading}>{aiLoading ? "…" : "Ask"}</Btn>
            </div>
            {aiRes && (
              <div data-testid="ai-search-result">
                <div className="text-[13px] text-slate-200 bg-slate-900/60 border border-slate-800 rounded-lg p-3 mb-3">{aiRes.answer}</div>
                {aiRes.devices.length > 0 && (
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    {aiRes.devices.map((dv) => (
                      <button key={dv.id} onClick={() => { setAiOpen(false); nav("/devices/" + dv.id); }} className="w-full flex items-center gap-2 text-left bg-slate-900/60 hover:bg-slate-800 rounded-md px-3 py-1.5 text-[13px]">
                        <StatusBadge status={dv.status} small />
                        <span className="font-mono text-slate-100">{dv.hostname}</span>
                        <span className="text-slate-500">{dv.customer}</span>
                        <span className="ml-auto text-[11px] text-slate-500">{dv.os}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
